import React, { useCallback, useRef, useState } from 'react';
import { Flex, Box, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { TableRow } from 'components/molecules/agGrid/components/tableRow';
import { ChevronDown, Copy } from 'lucide-react';
import ContentLoader from 'react-content-loader';
import { useTheme } from 'styled-components';
import { Tooltip } from '@getro/rombo';
import { Dropdown } from 'components/molecules/dropdown';
import { ContactEmailDropdownContent } from 'components/molecules/contactEmailDropdown/contactEmailDropdownContent';

export const ContactEmailRenderer = ({ data, value, colDef, ...props }) => {
  const { colors } = useTheme();
  const [copiedEmail, setCopiedEmail] = useState('');
  const ref = useRef(null);
  const label = colDef?.field === 'personalEmail' ? 'Personal' : 'Work';
  const entries = value.filter(Boolean).map((email) => ({ email, label }));

  const handleCopyEmail = useCallback((event, email) => {
    event.stopPropagation();
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(email);
      setCopiedEmail(email);
    }
  }, []);

  if (data.options.showLoader) {
    return (
      <ContentLoader
        speed={2}
        width={400}
        height={120}
        viewBox="0 0 400 120"
        backgroundColor={colors.neutral[50]}
        foregroundColor={colors.neutral[20]}
        {...props}
      >
        <rect x="9" y="18" rx="4" ry="4" width="80" height="8" />
      </ContentLoader>
    );
  }

  if (!value.length) {
    return '';
  }

  return (
    <TableRow {...props}>
      <Box
        display="grid"
        height="100%"
        width="100%"
        sx={{
          '&:hover #copy-email-icon': {
            display: 'block',
          },
        }}
      >
        <Flex p="8px" alignItems="center" justifyContent="space-between" sx={{ gap: '8px' }}>
          <Flex sx={{ gap: '8px', alignItems: 'center' }}>
            <Text
              fontSize="14px"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {value[0] || ''}
            </Text>

            {value.length > 1 && (
              <Dropdown
                ref={ref}
                triggerProps={{
                  variant: 'tertiary',
                  size: 'sm',
                }}
                placement="bottom-center"
                trigger={
                  <Box
                    sx={{
                      flexShrink: 0,
                      cursor: 'pointer',
                      borderRadius: '99px',
                      border: '1px solid',
                      borderColor: 'border.subtle',
                      bg: 'neutral.0',
                      color: 'text.main',
                      textDecoration: 'none',
                      p: '2px 8px',
                      display: 'flex',
                      maxHeight: '20px',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    +{value.length - 1}
                  </Box>
                }
              >
                <ContactEmailDropdownContent entries={entries} />
              </Dropdown>
            )}
          </Flex>

          {value.length === 1 && (
            <Box
              sx={{ cursor: 'pointer', outline: 'none', display: 'none' }}
              onClick={(event) => handleCopyEmail(event, value[0])}
              id="copy-email-icon"
            >
              <Tooltip
                sx={{ color: 'neutral.0', outline: 'none' }}
                triggerProps={{
                  sx: { outline: 'none' },
                }}
                position="right"
                content={<Text fontSize="12px">{copiedEmail === value.join(', ') ? 'Copied!' : 'Copy email'}</Text>}
                trigger={
                  <Box sx={{ outline: 'none', minWidth: '16px', minHeight: '16px', maxHeight: '16px' }}>
                    <Box as={Copy} size="16px" />
                  </Box>
                }
              />
            </Box>
          )}
          {value.length > 1 && (
            <Box
              sx={{ outline: 'none', minWidth: '16px', minHeight: '16px', maxHeight: '16px', cursor: 'pointer' }}
              onClick={() => ref.current.openDropdown()}
            >
              <Box as={ChevronDown} size="16px" sx={{ color: 'text.main' }} />
            </Box>
          )}
        </Flex>
      </Box>
    </TableRow>
  );
};

ContactEmailRenderer.propTypes = {
  data: PropTypes.object,
  value: PropTypes.array,
  colDef: PropTypes.object,
};

ContactEmailRenderer.defaultProps = {
  data: {},
  value: [],
  colDef: {},
};
